import React, { useState, useEffect } from "react";
import { NavLink, Link, useNavigate } from "react-router-dom";
import { NavDropdown, Button } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import logoIUH from "../../resource/img/Logo_IUH.png";
import { logoutUser } from "../../services/userServices";
import { logout } from "../../redux/slice/userSlice";
import { fetchSupportTicketsAdmin } from "../../services/adminService";
import axios from "../../customize/axios";
import NavAdmin from "../NavBar/NavAdmin";
import "./Nav.scss";

const NavHeader = () => {
  const admin = useSelector((state) => state.admin || state.user);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);

  const loadNotifications = async () => {
    try {
      const combinedNotis = [];
      let pendingCount = 0;

      // 1. Fetch pending support tickets
      const ticketRes = await fetchSupportTicketsAdmin();
      if (ticketRes) {
        const tickets = Array.isArray(ticketRes) ? ticketRes : (ticketRes.data || []);
        if (Array.isArray(tickets)) {
          const pendingTickets = tickets.filter((t) => t.status === "PENDING");
          pendingCount += pendingTickets.length;

          pendingTickets.forEach((t) => {
            combinedNotis.push({
              id: `ticket-${t._id || t.ticketCode}`,
              title: `Phiếu hỗ trợ mở khóa ví (${t.userCode})`,
              message: t.reason || "Yêu cầu mở khóa ví",
              createdAt: t.createdAt,
              isRead: false,
              link: "/admin/ewallet",
            });
          });
        }
      }

      // 2. Fetch admin notifications saved in DB
      try {
        const notiRes = await axios.get("/api/notifications/ADMIN");
        if (notiRes && Array.isArray(notiRes)) {
          notiRes.forEach((n) => {
            combinedNotis.push({
              id: n._id,
              title: n.title || "Thông báo hệ thống",
              message: n.message,
              createdAt: n.createdAt,
              isRead: n.isRead,
              link: "/admin/ewallet",
            });
            if (!n.isRead) pendingCount++;
          });
        }
      } catch (err) {
        console.log("Error fetching ADMIN notifications:", err);
      }

      // Sort by latest createdAt
      combinedNotis.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));

      setNotifications(combinedNotis);
      setUnreadCount(pendingCount);
    } catch (error) {
      console.log("Error loading admin notifications:", error);
    }
  };

  useEffect(() => {
    loadNotifications();
    const interval = setInterval(() => {
      loadNotifications();
    }, 15000);
    return () => clearInterval(interval);
  }, []);

  const handleMarkAllRead = async () => {
    try {
      await axios.put("/api/notifications/mark-all-read/ADMIN");
      setUnreadCount(0);
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
    } catch (error) {
      console.log("Error marking all read:", error);
    }
  };

  const handleNotiClick = (noti) => {
    setShowNotifications(false);
    if (noti.link) {
      navigate(noti.link);
    }
  };

  const handleLogout = async () => {
    try {
      const data = await logoutUser();

      dispatch(logout());
      toast.success(data?.message || "Đã đăng xuất tài khoản quản trị");
      navigate("/admin/login");
    } catch (err) {
      dispatch(logout());
      navigate("/admin/login");
    }
  };

  const adminName = admin?.adminData?.fullName || admin?.userData?.fullName || "Quản trị viên";
  const initialLetter = adminName ? adminName.charAt(0).toUpperCase() : "A";

  return (
    <>
      {/* TOPBAR */}
      <div className="topbar px-3 px-md-4 py-2 d-flex justify-content-between align-items-center text-white small">
        <div className="d-flex align-items-center gap-2">
          <i className="bi bi-building"></i>
          <span className="fw-medium">Trường Đại học Công nghiệp TP.HCM - Bãi Đỗ Xe Thông Minh IUH Parking</span>
        </div>
        <div className="d-none d-md-flex align-items-center gap-4">
          <span><i className="bi bi-telephone me-1"></i> 0283.8940 390 - ext 838</span>
          <span><i className="bi bi-envelope me-1"></i> csm@iuh.edu.vn</span>
        </div>
      </div>

      {/* MAIN NAVBAR */}
      <div className="main-header-navbar px-3 px-md-4 py-2 bg-white border-bottom shadow-sm d-flex justify-content-between align-items-center">
        <Link to="/" className="d-flex align-items-center gap-3 text-decoration-none">
          <img src={logoIUH} alt="IUH Logo" style={{ height: "48px", objectFit: "contain" }} />
          <div className="brand-text">
            <h5 className="fw-bold mb-0 text-primary tracking-wide">IUH Parking</h5>
            <span className="text-secondary small fw-semibold">ĐẠI HỌC CÔNG NGHIỆP TP.HCM</span>
          </div>
        </Link>

        <div className="d-flex align-items-center gap-3">
          {/* Notification Bell */}
          <div className="position-relative">
            <button
              className="btn btn-light rounded-circle p-2 d-flex align-items-center justify-content-center border"
              style={{ width: "40px", height: "40px" }}
              onClick={() => {
                setShowNotifications(!showNotifications);
                if (!showNotifications) loadNotifications();
              }}
            >
              <i className="bi bi-bell fs-5 text-secondary"></i>
              {unreadCount > 0 && (
                <span className="badge bg-danger rounded-circle position-absolute top-0 start-100 translate-middle">
                  {unreadCount}
                </span>
              )}
            </button>

            {showNotifications && (
              <div
                className="position-absolute bg-white border rounded-3 p-3 shadow-lg"
                style={{ right: 0, top: "50px", width: "340px", zIndex: 1050, maxHeight: "400px", overflowY: "auto" }}
              >
                <div className="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom">
                  <span className="fw-bold text-dark d-flex align-items-center gap-2">
                    <i className="bi bi-bell-fill text-primary"></i> Thông báo hệ thống
                  </span>
                  {unreadCount > 0 && (
                    <Button variant="link" size="sm" className="p-0 text-decoration-none text-primary fw-semibold" onClick={handleMarkAllRead}>
                      Đã đọc tất cả
                    </Button>
                  )}
                </div>
                {notifications.length === 0 ? (
                  <div className="text-center py-4 text-muted small">
                    <i className="bi bi-inbox fs-3 d-block mb-1 text-secondary opacity-50"></i>
                    Không có thông báo mới.
                  </div>
                ) : (
                  notifications.map((noti, idx) => (
                    <div
                      key={noti.id || idx}
                      className="p-2 mb-2 rounded border-bottom transition-all"
                      style={{ cursor: "pointer", backgroundColor: !noti.isRead ? "#fff8e6" : "#fff" }}
                      onClick={() => handleNotiClick(noti)}
                    >
                      <div className="d-flex justify-content-between align-items-start">
                        <strong className="d-block text-dark small">{noti.title}</strong>
                        {!noti.isRead && <span className="badge bg-danger rounded-pill ms-1" style={{ fontSize: "9px" }}>Mới</span>}
                      </div>
                      <p className="mb-1 text-secondary" style={{ fontSize: "12px" }}>{noti.message}</p>
                      <span className="text-muted" style={{ fontSize: "10px" }}>
                        {noti.createdAt ? new Date(noti.createdAt).toLocaleString("vi-VN") : ""}
                      </span>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          {/* User Account / Login */}
          {admin?.isAuthenticated ? (
            <div className="d-flex align-items-center gap-2">
              <div className="user-avatar-badge rounded-circle bg-primary text-white d-flex align-items-center justify-content-center fw-bold shadow-sm">
                <span>{initialLetter}</span>
              </div>

              <NavDropdown
                title={
                  <span className="fw-bold text-dark small ms-1">
                    {adminName}
                  </span>
                }
                id="admin-user-dropdown"
                align="end"
              >
                <NavDropdown.Item as={NavLink} to="/admin/infomation" className="py-2">
                  <i className="bi bi-person me-2"></i> Thông tin quản trị
                </NavDropdown.Item>
                <NavDropdown.Divider />
                <NavDropdown.Item onClick={handleLogout} className="text-danger py-2">
                  <i className="bi bi-box-arrow-right me-2"></i> Đăng xuất
                </NavDropdown.Item>
              </NavDropdown>
            </div>
          ) : (
            <Link to="/admin/login" className="btn btn-primary rounded-3 px-4 fw-bold">
              Đăng nhập
            </Link>
          )}
        </div>
      </div>

      {/* Sub-Navbar for Admin pages when authenticated */}
      {admin?.isAuthenticated && <NavAdmin />}
    </>
  );
};

export default NavHeader;
